// "Minhas pendências de duas chaves" (`/me/two-key`; Parte 5.7/5.9 "Usuário: pendências de duas chaves"; REQ-AUTHZ-015;
// GIY-056): aprovações aguardando a segunda chave que eu posso concluir (tenho a permissão do ato) e as em que já sou
// ator; a segunda chave genérica (`authz.two-key.self` + permissão do ato, step-up) aplica o ato na mesma transação.
// Mesma pessoa → `422 AUTHZ_TWO_KEY_SAME_ACTOR`; mesmo papel quando exigido → `422 AUTHZ_TWO_KEY_DISTINCT_ROLE_REQUIRED`.
import {
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@giyus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { ProblemAlert, TwoKeyBanner } from '../../../components';
import { useMe } from '../../../lib/auth/authContext';
import { useStepUpAction } from '../../../lib/auth/useStepUp';
import { useDateFormatter } from '../../../lib/dates/useDateFormatter';
import { AccountMeNav } from '../../../pages/Account';
import { type TwoKeyApproval, useTwoKeyApprovals, useTwoKeySecondKey } from '../api/queries';
import { StatusBadge } from '../components/StatusBadge';
import { TextDialog } from '../components/TextDialog';

export function MyTwoKeyPage() {
  const { t } = useTranslation(['authz', 'common']);
  const dates = useDateFormatter();
  const me = useMe();
  const awaiting = useTwoKeyApprovals(false, 'PendingSecondKey');
  const mine = useTwoKeyApprovals(true, 'PendingSecondKey');
  const secondKey = useTwoKeySecondKey();
  const [signing, setSigning] = useState<TwoKeyApproval | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const action = useStepUpAction<{ approvalId: string; comment: string | null }>({
    key: 'authz-two-key-second',
    run: async (input) => {
      await secondKey.mutateAsync(input);
      setSigning(null);
      setMessage(t('authz:me.twoKey.completed'));
    },
    toPayload: (input) => input,
    fromPayload: (payload) => {
      const value = payload as { approvalId?: unknown; comment?: unknown } | null;
      return value && typeof value.approvalId === 'string'
        ? {
            approvalId: value.approvalId,
            comment: typeof value.comment === 'string' ? value.comment : null,
          }
        : null;
    },
  });

  const renderTable = (items: readonly TwoKeyApproval[], testId: string, canSign: boolean) =>
    items.length === 0 ? (
      <p className="text-body text-muted-foreground" data-testid={`${testId}-empty`}>
        {t('authz:me.twoKey.empty')}
      </p>
    ) : (
      <Table data-testid={testId}>
        <TableHeader>
          <TableRow>
            <TableHead>{t('authz:me.twoKey.columns.action')}</TableHead>
            <TableHead>{t('authz:me.twoKey.columns.object')}</TableHead>
            <TableHead>{t('authz:me.twoKey.columns.firstKey')}</TableHead>
            <TableHead>{t('authz:me.twoKey.columns.expires')}</TableHead>
            <TableHead>{t('authz:me.twoKey.columns.status')}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((approval) => (
            <TableRow key={approval.id} data-testid={`two-key-row-${approval.id}`}>
              <TableCell>
                {t(`authz:me.twoKey.actions.${approval.actionType}`, {
                  defaultValue: approval.actionType,
                })}
              </TableCell>
              <TableCell className="font-mono text-caption">
                {approval.objectType} · {approval.objectId}
              </TableCell>
              <TableCell>
                {approval.firstActorUserId === me.user.id
                  ? t('authz:me.twoKey.you')
                  : approval.firstActorUserId}
                <span className="block text-caption text-muted-foreground">
                  {dates.dateTime(approval.firstAt)}
                </span>
              </TableCell>
              <TableCell>{dates.dateTime(approval.expiresAt)}</TableCell>
              <TableCell>
                <StatusBadge status={approval.status} />
              </TableCell>
              <TableCell>
                {canSign && (
                  <Button
                    type="button"
                    size="sm"
                    data-testid={`two-key-sign-${approval.id}`}
                    onClick={() => {
                      secondKey.reset();
                      setMessage(null);
                      setSigning(approval);
                    }}
                  >
                    {t('authz:me.twoKey.actions.sign')}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );

  return (
    <section className="flex flex-col gap-4" data-testid="my-two-key">
      <Link className="text-body text-muted-foreground underline-offset-2 hover:underline" to="/me">
        {t('authz:me.back')}
      </Link>
      <div>
        <h1 className="text-headline font-semibold tracking-tight">{t('authz:me.twoKey.title')}</h1>
        <p className="text-muted-foreground">{t('authz:me.twoKey.subtitle')}</p>
      </div>
      <AccountMeNav />
      {message && (
        <p role="status" className="text-body" data-testid="two-key-message">
          {message}
        </p>
      )}
      <h2 className="text-body font-semibold">{t('authz:me.twoKey.awaitingMe')}</h2>
      {awaiting.isError ? (
        <ProblemAlert
          error={awaiting.error}
          onRetry={() => {
            void awaiting.refetch();
          }}
        />
      ) : awaiting.isLoading || !awaiting.data ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        renderTable(awaiting.data, 'two-key-awaiting', true)
      )}
      <h2 className="text-body font-semibold">{t('authz:me.twoKey.mine')}</h2>
      {mine.isError ? (
        <ProblemAlert
          error={mine.error}
          onRetry={() => {
            void mine.refetch();
          }}
        />
      ) : mine.isLoading || !mine.data ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        renderTable(mine.data, 'two-key-mine', false)
      )}
      {signing && (
        <TwoKeyBanner
          firstKeyBy={signing.firstActorUserId}
          firstKeyAt={dates.dateTime(signing.firstAt)}
          deadline={dates.dateTime(signing.expiresAt)}
        />
      )}
      <TextDialog
        open={signing !== null}
        onOpenChange={(next) => {
          if (!next) {
            setSigning(null);
          }
        }}
        title={t('authz:me.twoKey.signDialog.title')}
        description={t('authz:me.twoKey.signDialog.description', {
          action: signing?.actionType ?? '',
          object: signing?.objectId ?? '',
        })}
        textLabel={t('authz:me.twoKey.signDialog.comment')}
        textRequired={false}
        confirmLabel={t('authz:me.twoKey.actions.sign')}
        pending={secondKey.isPending}
        error={secondKey.error}
        testId="two-key-dialog"
        onConfirm={(comment) => {
          if (signing) {
            action.execute({ approvalId: signing.id, comment }).catch(() => {
              // O erro fica no diálogo.
            });
          }
        }}
      />
    </section>
  );
}
